import { defineStore } from 'pinia';
import { usePinia } from '#imports';
import { handleErrorAndNotificationHandler } from '~/util/errorNotificationHandler';

export const useCommentsStore = defineStore('comments', {
  state: () => ({
    comments: {},
    sentComment: '',
    getCommentsByPost: {},
    postCommentActions: {},
    postCommentAttachment: '',
    postCommentAuthors: [],
    singlePostComment: {},
  }),
  actions: {
    async getComment(commentId) {
      try {
        const resp = await usePinia().$axios.$get(
          usePinia().$API.comments.setCommentId(commentId)
        );

        this.comments = resp;
      } catch (error) {
        handleErrorAndNotificationHandler('Error fetching comments:', error);
      }
    },
    async commentsByPost({
      postId,
      page,
      pageSize,
      sort,
      method,
      parentCommentId,
    }) {
      try {
        const pinia = usePinia();
        const Query = pinia.$Query;
        if (method === 'GET') {
          const resp = await pinia.$axios.$get(
            pinia.$API.comments.getPostComments(
              postId,
              new Query({ sort, page, pageSize }).getQueryParameters()
            )
          );
          this.getCommentsByPost = resp;
        } else if (method === 'POST') {
          const formData = new FormData();
          const stringify = JSON.stringify({
            commentText: this.sentComment.commentText,
          });
          formData.append('commentTextRequest', stringify);
          formData.append('file', this.sentComment.file);
          await pinia.$axios.$post(
            pinia.$API.comments.getPostComments(
              postId,
              new Query({ parentCommentId }).getQueryParameters()
            ),
            formData,

            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
        } else if (method === 'DELETE') {
          await pinia.$axios.$delete(
            pinia.$API.comments.getPostComments(postId)
          );
        }
      } catch (error) {
        handleErrorAndNotificationHandler('Error in comments by post:', error);
      }
    },
    async getSingleComment({ postId, commentId }) {
      try {
        const pinia = usePinia();
        const resp = await pinia.$axios.$get(
          pinia.$API.comments.getSinglePostComment(postId, commentId)
        );

        this.singlePostComment = resp;
        const match = this.getCommentsByPost.items.find(
          (comment) => comment.commentId === resp.commentId
        );
        if (match) {
          this.getCommentsByPost = {
            ...this.getCommentsByPost,
            items: this.getCommentsByPost.items.filter(
              (comment) => comment.commentId !== resp.commentId
            ),
            totalCount: this.getCommentsByPost.totalCount - 1,
          };
        }
        this.getCommentsByPost = {
          ...this.getCommentsByPost,
          items: [resp, ...this.getCommentsByPost.items],
          totalCount: this.getCommentsByPost.totalCount + 1,
        };
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching single post comment:',
          error
        );
      }
    },
    async updatePostComment({ postId, commentId }) {
      try {
        const formData = new FormData();
        const stringify = JSON.stringify({
          commentText: this.sentComment.commentText,
        });
        formData.append('commentTextRequest', stringify);
        formData.append('file', this.sentComment.file);
        await usePinia().$axios.$patch(
          usePinia().$API.comments.getSinglePostComment(postId, commentId),
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error updating post comment:',
          error
        );
      }
    },
    async deletePostComment({ postId, commentId }) {
      try {
        await usePinia().$axios.$delete(
          usePinia().$API.comments.getSinglePostComment(postId, commentId)
        );
        this.getCommentsByPost.totalCount -= 1;
        this.getCommentsByPost.items = this.getCommentsByPost.items.filter(
          (comment) => comment.commentId !== commentId
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error deleting post comment:',
          error
        );
      }
    },
    async getCommentsPostActions({ postId, commentId }) {
      try {
        const resp = await usePinia().$axios.$get(
          usePinia().$API.comments.postCommentsAction(postId, commentId)
        );
        this.postCommentActions = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching comments post actionns:',
          error
        );
      }
    },
    async downloadCommentPostAttachment({ postId, commentId }) {
      try {
        const resp = await usePinia().$axios.$get(
          usePinia().$API.comments.postCommentAttachmentDownload(
            postId,
            commentId
          ),
          {
            responseType: 'arraybuffer', // Ensure response is treated as an ArrayBuffer
          }
        );
        const type = resp.headers['content-type'];
        // Create a Blob from the ArrayBuffer
        const blob = new Blob([resp], { type });

        // Create a temporary anchor element
        const anchor = document.createElement('a');
        anchor.href = URL.createObjectURL(blob); // Create a URL for the Blob
        anchor.setAttribute('download', 'downloaded_file.png'); // Set a default filename

        // Programmatically click on the anchor to initiate the download
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        // Optionally, you can commit the response if needed
        this.postCommentAttachment = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error downloading comment post attachment:',
          error
        );
      }
    },
    async removePostCommentAttachment({ postId, commentId }) {
      try {
        await usePinia().$axios.$delete(
          usePinia().$API.comments.removePostCommentAttachment(
            postId,
            commentId
          )
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error removing post comment attachment:',
          error
        );
      }
    },
    async getPostCommentAuthors({ postId }) {
      try {
        const resp = await usePinia().$axios.$get(
          usePinia().$API.comments.postCommentAuthors(postId)
        );
        this.postCommentAuthors = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching post comment authors:',
          error
        );
      }
    },
    async subscribeToPostComments({ postId, method }) {
      try {
        const pinia = usePinia();
        if (method === 'POST') {
          await pinia.$axios.$post(
            pinia.$API.user.subscribeUserToPostComments(postId)
          );
        } else if (method === 'DELETE') {
          await pinia.$axios.$delete(
            pinia.$API.user.subscribeUserToPostComments(postId)
          );
        }
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error subscribing to post comments:',
          error
        );
      }
    },
    resetComments() {
      this.discussionAsset = [];
      this.getCommentsByPost = [];
    },
    setComment(sentComment) {
      this.sentComment = sentComment;
    },
    removeComment(commentId) {
      this.getCommentsByPost.items = this.getCommentsByPost.items.filter(
        (c) => c.commentId !== commentId
      );
    },
  },

  getters: {
    getCommentsWithRepliesGetter: (state) => {
      return state.comments;
    },
  },
});
